import { useMutation, useQuery } from '@tanstack/vue-query';
import { Type, type Static } from '@sinclair/typebox';
import { validatorFactory } from '@laam/lib/validator';

const CouponsSuccessSchema = Type.Object({
	success: Type.Object({
		discount: Type.Number(),
		type: Type.String(),
		discount_type: Type.String(),
		message: Type.String(),
	}),
});

const CouponsFailureSchema = Type.Object({
	detail: Type.Object({
		error: Type.String(),
	}),
});

const CouponsListSchema = Type.Object({
	item: Type.Array(
		Type.Object({
			code: Type.String(),
			description: Type.String(),
			min_cart_value: Type.Union([Type.Number(), Type.Null()]),
			on_first_order: Type.Boolean(),
			platform: Type.String(),
			type: Type.String(),
			discount: Type.Number(),
			discount_type: Type.String(),
			visibility: Type.Optional(Type.Boolean()),
			max_cap: Type.Optional(Type.Number()),
		}),
	),
});
const CouponsValidatorSuccess =
	validatorFactory<Static<typeof CouponsSuccessSchema>>(CouponsSuccessSchema);

const CouponsValidatorFailure =
	validatorFactory<Static<typeof CouponsFailureSchema>>(CouponsFailureSchema);

const CouponsListValidator =
	validatorFactory<Static<typeof CouponsListSchema>>(CouponsListSchema);

interface Headers {
	[key: string]: string;
}
export const useCouponsMutation = (headers: Ref<Headers>) => {
	const { data, error, isPending, status, mutateAsync } = useMutation({
		mutationKey: ['place_order'],
		gcTime: Infinity,
		retry: 1,
		mutationFn: async (payload: {
			buyer_id: string;
			coupon_code: string;
			country_code: string;
			order_total: number;
			email: string;
		}) => {
			try {
				const api = createApiInstance();
				const response = await api.post('coupons/validate', payload, {
					headers: headers!.value,
				});
				if (response.status === 200) {
					try {
						return CouponsValidatorSuccess.verify(response.data);
					} catch (error) {
						console.log(error);
						throw 'err';
					}
				} else if (response.status === 400) {
					try {
						return CouponsValidatorFailure.verify(response.data);
					} catch (error) {
						console.log(error);
						throw 'err';
					}
				}
				throw 'err';
			} catch (error) {
				console.log(error);
			}
			throw 'err';
		},
	});
	return {
		data,
		error,
		status,
		isPending,
		mutateAsync,
	};
};

export const useGetCouponsQuery = (
	countryCode: Ref<string>,
	loganId: Ref<string | undefined>,
) => {
	const { data, error, isLoading, isFetched, refetch, suspense } = useQuery({
		queryKey: ['countryCode', countryCode],
		staleTime: 300000, // 5 minutes
		gcTime: 1,
		refetchOnWindowFocus: false,
		refetchInterval: 300000,
		queryFn: async () => {
			const api = createApiInstance();
			try {
				let url = `coupons/user?country_code=${countryCode.value}`;

				if (loganId.value != null) {
					url += `&buyer_id=${loganId.value}`;
				}

				const response = await api.get(url);

				if (response.status === 200) {
					return CouponsListValidator.verify({
						item: response.data,
					});
				}

				return;
			} catch (error) {
				console.log(error);
				throw error;
			}
		},
	});

	return {
		data,
		error,
		refetch,
		suspense,
		isLoading,
		isFetched,
	};
};
